import { useState, useRef, useEffect, useCallback } from 'react';

import { TreeEntry, isTreeFolder, TreeFolder } from './document-list-to-tree';

/**
 * Custom hook for managing folder state and accordion functionality
 *
 * @param entry - The tree entry to manage state for
 * @returns Object containing folder state and management functions, or null if entry is not a folder
 */
export const useCategoryFolder = (entry: TreeEntry) => {
  const [open, setOpen] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const accordionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate dynamic height for smooth accordion animation
  useEffect(() => {
    if (open) {
      const nextHeight = contentRef.current?.scrollHeight ?? 0;
      setContentHeight(nextHeight);
    } else {
      setContentHeight(0);
    }
  }, [open]);

  // Observe content size changes while open (e.g., nested folders expanding)
  useEffect(() => {
    if (!contentRef.current) return;
    const node = contentRef.current;
    const observer = new ResizeObserver(() => {
      if (open) {
        const nextHeight = node.scrollHeight;
        setContentHeight(nextHeight);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [open]);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  // Validate entry is actually a folder - return null after all hooks have been called
  if (!isTreeFolder(entry)) {
    console.warn('Category component received non-folder entry:', entry);
    return null;
  }

  return {
    entry: entry as TreeFolder,
    isOpen: open,
    toggleOpen,
    contentHeight,
    accordionRef,
    contentRef,
  };
};
